import { IconButton, Tooltip, Box, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import {
  canEditCampaign,
  canDeleteCampaign,
  canLaunchCampaign,
} from "../constants";
import { memo } from "react";
import PropTypes from "prop-types";

const pulseKeyframe = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.25); }
  100% { transform: scale(1); }
`;

export const CampaignActionMenuTable = memo(({
  campaign,
  onView,
  onEdit,
  onDelete,
  onLaunch,
  onClone,
}) => {
  const theme = useTheme();
  const status = campaign?.estado;

  const isEditable = canEditCampaign(status);
  const isDeletable = canDeleteCampaign(status);
  const isLaunchable = canLaunchCampaign(status);

  const handleView = () => {
    onView?.(campaign);
  };

  const handleEdit = () => {
    onEdit?.(campaign);
  };

  const handleDelete = () => {
    onDelete?.(campaign);
  };

  const handleLaunch = () => {
    onLaunch?.(campaign);
  };

  const handleClone = () => {
    onClone?.(campaign);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Tooltip title="Clonar campaña" arrow>
        <IconButton
          aria-label="clonar"
          size="small"
          onClick={handleClone}
          color="primary"
        >
          <FileCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Ver detalles" arrow>
        <IconButton
          aria-label="ver detalles"
          size="small"
          onClick={handleView}
          color="primary"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip
        title={isEditable ? "Editar" : "No se puede editar esta campaña"}
        arrow
      >
        <span>
          <IconButton
            aria-label="editar"
            size="small"
            onClick={handleEdit}
            color="primary"
            disabled={!isEditable}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip
        title={
          isLaunchable ? "Lanzar campaña" : "No se puede lanzar esta campaña"
        }
        arrow
      >
        <span>
          <IconButton
            aria-label="lanzar"
            size="small"
            onClick={handleLaunch}
            color="success"
            disabled={!isLaunchable}
          >
            <RocketLaunchIcon
              fontSize="small"
              sx={{
                animation: isLaunchable
                  ? `${pulseKeyframe} 1.5s ease-in-out infinite`
                  : undefined,
                filter: isLaunchable
                  ? `drop-shadow(0 0 6px ${theme.palette.primary.main})`
                  : undefined,
              }}
            />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip
        title={
          isDeletable
            ? "Eliminar/Cancelar"
            : "No se puede eliminar esta campaña"
        }
        arrow
      >
        <span>
          <IconButton
            aria-label="eliminar"
            size="small"
            onClick={handleDelete}
            disabled={!isDeletable}
          >
            <DeleteIcon
              fontSize="small"
              color={isDeletable ? "error" : "disabled"}
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
});

CampaignActionMenuTable.displayName = "CampaignActionMenuTable";

CampaignActionMenuTable.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    nombre: PropTypes.string,
    estado: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onLaunch: PropTypes.func,
  onClone: PropTypes.func,
};
